import { useIssueToolMutation } from "@common/api/tools"
import { currentUserQuery, userQuery } from "@common/api/users"
import { UserInitials } from "@common/components/shared/user-initials"
import { Input } from "@common/components/ui/input"
import {
  formatShortDate,
  toDateInputValue,
  todayInputValue,
} from "@common/lib/date-utils"
import { handleError } from "@common/utils"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { addDays } from "date-fns"
import { useState } from "react"
import useCustomToast from "@/hooks/use-custom-toast"
import { UserPicker } from "@/routes/_layout/quotes_/$quoteId/-components/user-picker"
import { ActionCard, ActionField, useCloseTo } from "../-components/action-card"
import { type Booking, WorkOrderPicker } from "../-components/work-order-picker"

export const Route = createFileRoute("/_layout/tools/$toolId/issue")({
  component: IssueTool,
})

function IssueTool(): React.ReactElement {
  const { toolId } = Route.useParams()
  const showToast = useCustomToast()
  const mutation = useIssueToolMutation()
  const { data: me } = useQuery(currentUserQuery())
  const [custodianId, setCustodianId] = useState<string | null>(null)
  const [booking, setBooking] = useState<Booking | null>(null)
  const [dueOn, setDueOn] = useState(toDateInputValue(addDays(new Date(), 1)))
  const { data: custodian } = useQuery({
    ...userQuery({ user_id: custodianId ?? "" }),
    enabled: Boolean(custodianId),
  })
  const close = useCloseTo()

  const issue = () =>
    custodianId &&
    booking &&
    mutation.mutate(
      {
        tool_id: toolId,
        toolIssue: {
          custodian_id: custodianId,
          work_package_id: booking.workPackageId,
          work_task_id: booking.workTaskId,
          due_on: dueOn,
        },
      },
      {
        onSuccess: (tool) => {
          showToast("Issued", `${tool.number} is on loan.`, "success")
          close()
        },
        onError: (error) => handleError(error, showToast),
      },
    )

  return (
    <ActionCard
      title={
        me && (
          <>
            <span className="text-muted-foreground">Issued by</span>
            <UserInitials user={me} />
            {me.first_name} {me.last_name}
            <span className="text-muted-foreground">
              · {formatShortDate(new Date())}
            </span>
          </>
        )
      }
      confirmLabel="Issue tool"
      ready={Boolean(custodianId && booking && dueOn)}
      pending={mutation.isPending}
      onCancel={close}
      onConfirm={issue}
    >
      <div className="grid grid-cols-2 gap-2">
        <ActionField label="Technician">
          {() => (
            <UserPicker
              label="Technician"
              user={custodianId && custodian ? custodian : null}
              disabled={false}
              className="w-full bg-background"
              onPick={setCustodianId}
            />
          )}
        </ActionField>
        <ActionField label="Due back">
          {(id) => (
            <Input
              id={id}
              type="date"
              value={dueOn}
              min={todayInputValue()}
              onChange={(event) => setDueOn(event.target.value)}
            />
          )}
        </ActionField>
      </div>
      <ActionField label="Booked to">
        {(id) => (
          <WorkOrderPicker id={id} booking={booking} onPick={setBooking} />
        )}
      </ActionField>
    </ActionCard>
  )
}
